import React from "react";
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { FieldArray, FieldArrayRenderProps } from "formik";
import { ConditionItem } from "./ConditionItem";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { canRemoveCondition } from "../utils/conditionUtils";
import { ConditionGroup } from "stores/data/insightAlerts/insightAlertModel";
import AddIcon from "@material-ui/icons/Add";
import { Info } from "lucide-react";

interface ConditionGroupProps {
  group: ConditionGroup;
  groupIndex: number;
  environmentalVariables?: IEnvironmentalVariable[];
  onConditionOperatorChange: (value: string) => void;
  onConditionUpdate: (
    conditionIndex: number,
    field: string,
    value: any
  ) => void;
  onAddCondition: () => void;
  onRemoveCondition: (conditionIndex: number) => void;
  t: (key: string) => string;
  errors?: any;
  /** Formik field name for the condition groups array. Defaults to "conditionGroups". */
  fieldArrayName?: string;
}

export const Group: React.FC<ConditionGroupProps> = ({
  group,
  groupIndex,
  environmentalVariables,
  onConditionOperatorChange,
  onConditionUpdate,
  onAddCondition,
  onRemoveCondition,
  errors,
  t,
  fieldArrayName = "conditionGroups",
}) => {
  return (
    <Box borderRadius={8}>
      <Stack spacing={4}>
        <Text size="md" fontWeight={"bold"} color="newBlue.500">
          {t("group")} {groupIndex + 1}
        </Text>

        <Flex gap={2} alignItems="center">
          <Text color="newBlue.500">{t("type")}</Text>
          <Select
            width="80px"
            borderRadius={8}
            backgroundColor="#ffffff"
            borderColor="newBlue.500"
            fontSize={14}
            size="sm"
            color="rgba(30, 82, 151, 0.8)"
            _focus={{ borderColor: "none" }}
            value={group.conditionOperator}
            onChange={(e) => onConditionOperatorChange(e.target.value)}
            disabled={group.conditions.length <= 1}
          >
            <option value={"and"}>{t("and")}</option>
            <option value={"or"}>{t("or")}</option>
          </Select>
          <Tooltip
            label={t("andVerifyConditionTrueOrVerifyAtLeastConditionTrue")}
            fontSize="md"
            hasArrow
          >
            <Info color={"#7e7b7b"} size="20px" />
          </Tooltip>
        </Flex>

        <FieldArray name={`${fieldArrayName}[${groupIndex}].conditions`}>
          {(conditionHelpers: FieldArrayRenderProps) => (
            <Stack spacing={3}>
              {group.conditions.map((condition, conditionIndex) => (
                <Box
                  key={`${condition.environmentalVariableId}-${conditionIndex}`}
                >
                  {conditionIndex > 0 && (
                    <Center py={2} mb={2}>
                      <Divider borderColor="newBlue.500" />
                      <Text
                        fontSize="md"
                        fontWeight={"bold"}
                        color="newBlue.500"
                        px={2}
                      >
                        {t(`${group.conditionOperator}`)}
                      </Text>
                      <Divider borderColor="newBlue.500" />
                    </Center>
                  )}
                  <ConditionItem
                    condition={condition}
                    environmentalVariables={environmentalVariables}
                    onUpdate={(field, value) =>
                      onConditionUpdate(conditionIndex, field, value)
                    }
                    onRemove={() => {
                      onRemoveCondition(conditionIndex);
                    }}
                    canRemove={canRemoveCondition(
                      group.conditions,
                      conditionIndex
                    )}
                    t={t}
                    conditionIndex={conditionIndex}
                    groupIndex={groupIndex}
                    errors={errors}
                    fieldArrayName={fieldArrayName}
                  />
                </Box>
              ))}

              <Center>
                <Button
                  layerStyle="lightOcean"
                  _hover={{
                    layerStyle: "lightOcean",
                  }}
                  onClick={() => {
                    onAddCondition();
                  }}
                  leftIcon={<AddIcon />}
                >
                  {t("addNewCondition")}
                </Button>
              </Center>
            </Stack>
          )}
        </FieldArray>
      </Stack>
    </Box>
  );
};
